import React from 'react';
import { BTN } from '../../constants/buttons';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../../constants/overlayLayers';

/**
 * THE shell of the three whole-order hire dialogs — Off hire all, Extend all…
 * and Bring all back on hire (2026-09-28). Extracted from `OffHireAllDialog.js`
 * (the reviewed pattern) when the other two arrived, so the modal chrome, the
 * error box, the "all or nothing" line and the Cancel/Confirm pair are ONE
 * definition rather than three copies that drift.
 *
 * The dialog owns its content and its submit; this owns only the frame.
 * Every testid is `${testIdPrefix}-dialog|-error|-cancel|-confirm`, which is
 * byte-for-byte what `OffHireAllDialog` stamped before the extraction.
 *
 * `tone` picks the header colour, matching the single-line `HireActionModal`
 * for the same verb: `green` off hire, `blue` extend, `indigo` bring back.
 * The confirm is `BTN.confirm` in every case — each one commits a change
 * (owner ruling 2026-09-25: a save/commit is the green confirm).
 */
const TONE = {
  green: { header: 'bg-green-50 dark:bg-green-900/20', title: 'text-green-800 dark:text-green-300' },
  blue: { header: 'bg-blue-50 dark:bg-blue-900/20', title: 'text-blue-800 dark:text-blue-300' },
  indigo: { header: 'bg-indigo-50 dark:bg-indigo-900/20', title: 'text-indigo-800 dark:text-indigo-300' },
};

export default function HireBulkDialogFrame({
  testIdPrefix, title, subtitle, tone = 'green', onSubmit, onCancel, loading = false, error = '',
  footnote = 'All or nothing: if any line cannot be changed, none are.', confirmLabel, busyLabel, children,
}) {
  const t = TONE[tone] || TONE.green;
  const titleId = `${testIdPrefix}-title`;
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black/50 ${MODAL_OVERLAY}`} data-testid={`${testIdPrefix}-dialog`}>
      <div
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-0 md:mx-4 max-h-[90vh] flex flex-col ${MODAL_PANEL_MOBILE_CAP}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className={`px-4 md:px-5 py-4 border-b border-gray-200 dark:border-gray-700 ${t.header} rounded-t-xl`}>
          <h3 id={titleId} className={`text-lg font-semibold ${t.title}`}>{title}</h3>
          <p className="text-sm mt-0.5 text-gray-600 dark:text-gray-400">{subtitle}</p>
        </div>

        {/* `noValidate`: the refusals are the inline ones (house convention). */}
        <form onSubmit={onSubmit} noValidate className="flex flex-col min-h-0">
          <div className="px-4 md:px-5 py-4 overflow-y-auto space-y-4">
            {children}
          </div>

          <div className="px-4 md:px-5 py-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
            {error && (
              <p className="text-sm border rounded-lg p-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700" data-testid={`${testIdPrefix}-error`}>
                {error}
              </p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400">{footnote}</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className={`flex-1 justify-center ${BTN.secondary}`}
                data-testid={`${testIdPrefix}-cancel`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 justify-center ${BTN.confirm} ${loading ? 'opacity-50 cursor-wait' : ''}`}
                data-testid={`${testIdPrefix}-confirm`}
              >
                {loading ? busyLabel : confirmLabel}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
